import React, { useEffect, useState } from "react";
import { FiEye, FiSearch } from "react-icons/fi";
import { getAuditLogs } from "../../services/api";
import globalStyles, { COLORS } from "../../styles/globalStyles";
import { getPageNumbers } from "../../helpers/Helpers";

const s = {
  operationBadge: (action) => {
    const operationStyle = {
      create: { background: "#dcfce7", color: "#059669" },
      update: { background: "#fef3c7", color: "#d97706" },
      delete: { background: "#fee2e2", color: "#ef4444" },
    };

    return {
      padding: "4px 8px",
      borderRadius: 6,
      fontSize: 12,
      fontWeight: 700,
      textTransform: "capitalize",
      ...(operationStyle[action] || {
        background: "#f8fafc",
        color: "#64748b",
      }),
    };
  },

  pre: {
    fontSize: 12,
    background: "#fff",
    padding: 10,
    borderRadius: 8,
    maxHeight: 220,
    overflow: "auto",
    margin: 0,
    whiteSpace: "pre-wrap",
    wordBreak: "break-word",
  },
};

const formatLabel = (value) => {
  if (!value) return "—";

  return String(value)
    .replace(/_/g, " ")
    .split(" ")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
};

const useIsMobile = (breakpoint = 768) => {
  const [isMobile, setIsMobile] = useState(window.innerWidth <= breakpoint);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= breakpoint);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [breakpoint]);

  return isMobile;
};

export default function Audit() {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ entityType: "", action: "" });
  const [search, setSearch] = useState("");
  const [view, setView] = useState(null);
  const [page, setPage] = useState(1);
  const [limit] = useState(20);

  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [isLastPage, setIsLastPage] = useState(false);
  const isMobile = useIsMobile();

  const load = () => {
    setLoading(true);

    const params = {
      page,
      limit,
    };

    if (search) params.search = search;
    if (filters.entityType) params.entityType = filters.entityType;
    if (filters.action) params.action = filters.action;

    getAuditLogs(params)
      .then((r) => {
        const data = r?.data?.data || {};

        setLogs(data.logs || []);
        setTotal(data.total || 0);
        setTotalPages(data.totalPages || 1);
        setIsLastPage(Boolean(data.isLastPage));
      })
      .catch(console.error)
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    load();
  }, [page, search, filters]);

  return (
    <div style={globalStyles.contentWrapper}>
      <div style={globalStyles.row}>
        <h1 style={globalStyles.h1}>Audit Logs</h1>
      </div>

      <div
        style={{
          ...globalStyles.toolbar,
          ...(isMobile ? globalStyles.responsive.mobileToolbar : {}),
        }}
      >
        <div
          style={{
            ...globalStyles.filterLeft,
            ...(isMobile ? globalStyles.responsive.mobileFilterLeft : {}),
          }}
        >
          <div
            style={{
              ...globalStyles.searchWrap,
              ...(isMobile ? globalStyles.responsive.mobileSearchWrap : {}),
            }}
          >
            <FiSearch style={globalStyles.searchIcon} />

            <input
              style={globalStyles.searchInput}
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setPage(1);
              }}
              placeholder="Search audit logs..."
            />
          </div>

          <select
            style={{
              ...globalStyles.filterSelect,
              ...(isMobile ? globalStyles.responsive.mobileFilterSelect : {}),
            }}
            value={filters.entityType}
            onChange={(e) => {
              setFilters({ ...filters, entityType: e.target.value });
              setPage(1);
            }}
          >
            <option value="">All Entities</option>
            {["User", "Operator", "Lead", "Demo", "Media", "Config"].map(
              (entity) => (
                <option key={entity} value={entity}>
                  {entity}
                </option>
              ),
            )}
          </select>

          <select
            style={{
              ...globalStyles.filterSelect,
              ...(isMobile ? globalStyles.responsive.mobileFilterSelect : {}),
            }}
            value={filters.action}
            onChange={(e) => {
              setFilters({ ...filters, action: e.target.value });
              setPage(1);
            }}
          >
            <option value="">All Operations</option>
            <option value="create">Create</option>
            <option value="update">Update</option>
            <option value="delete">Delete</option>
          </select>
        </div>
        <span style={globalStyles.paginatePageInfo}>
          Page {page} of {totalPages}
        </span>
      </div>

      {loading ? (
        <div style={globalStyles.loading}>Loading...</div>
      ) : (
        <div style={globalStyles.tableScroll}>
          <table style={globalStyles.table}>
            <thead>
              <tr>
                <th style={globalStyles.th}>Timestamp</th>
                <th style={globalStyles.th}>Entity</th>
                <th style={globalStyles.th}>Operation</th>
                <th style={globalStyles.th}>By</th>
                <th style={globalStyles.th}>Action</th>
              </tr>
            </thead>

            <tbody>
              {logs.length > 0 ? (
                logs.map((log) => (
                  <tr key={log._id}>
                    <td style={globalStyles.td}>
                      {log.timestamp
                        ? new Date(log.timestamp).toLocaleString()
                        : "—"}
                    </td>

                    <td style={globalStyles.td}>
                      <strong>{log.entityType || "—"}</strong>
                      <br />
                      <span style={{ fontSize: 11, color: "#94a3b8" }}>
                        {log.entityId || "—"}
                      </span>
                    </td>

                    <td style={globalStyles.td}>
                      <span style={s.operationBadge(log.action)}>
                        {formatLabel(log.action)}
                      </span>
                    </td>

                    <td style={globalStyles.td}>{log.userId?.name || "—"}</td>

                    <td style={globalStyles.td}>
                      <div style={globalStyles.btnRow}>
                        <button
                          type="button"
                          title="View"
                          style={globalStyles.viewBtn}
                          onClick={() => setView(log)}
                        >
                          <FiEye size={14} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td
                    colSpan="5"
                    style={{ ...globalStyles.td, ...globalStyles.emptyState }}
                  >
                    No audit logs found
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {!loading && totalPages > 1 && (
        <div style={globalStyles.paginateRow}>
          <button
            type="button"
            style={{
              ...globalStyles.paginateBtn,
              opacity: page === 1 ? 0.5 : 1,
            }}
            disabled={page === 1}
            onClick={() => setPage(1)}
          >
            {"<<"}
          </button>

          <button
            type="button"
            style={{
              ...globalStyles.paginateBtn,
              opacity: page === 1 ? 0.5 : 1,
            }}
            disabled={page === 1}
            onClick={() => setPage((prev) => prev - 1)}
          >
            {"<"}
          </button>

          {getPageNumbers(page, totalPages).map((pageNumber) => (
            <button
              key={pageNumber}
              type="button"
              style={{
                ...globalStyles.paginateBtn,
                background: page === pageNumber ? COLORS.primary : "#f8fafc",
                color: page === pageNumber ? "#fff" : COLORS.primary,
                minWidth: 36,
              }}
              onClick={() => setPage(pageNumber)}
            >
              {pageNumber}
            </button>
          ))}

          <button
            type="button"
            style={{
              ...globalStyles.paginateBtn,
              opacity: isLastPage ? 0.5 : 1,
            }}
            disabled={isLastPage}
            onClick={() => setPage((prev) => prev + 1)}
          >
            {">"}
          </button>

          <button
            type="button"
            style={{
              ...globalStyles.paginateBtn,
              opacity: isLastPage ? 0.5 : 1,
            }}
            disabled={isLastPage}
            onClick={() => setPage(totalPages)}
          >
            {">>"}
          </button>
        </div>
      )}

      {view && (
        <div style={globalStyles.modal}>
          <div
            style={{
              ...globalStyles.mcard,
              ...(isMobile ? globalStyles.responsive.mobileModalCard : {}),
            }}
          >
            <div style={globalStyles.modalHeader}>
              <h2 style={globalStyles.modalTitle}>View Audit Log</h2>

              <button
                type="button"
                style={globalStyles.modalCloseBtn}
                onClick={() => setView(null)}
              >
                ✕
              </button>
            </div>

            <div
              style={{
                ...globalStyles.viewGrid,
                ...(isMobile ? { gridTemplateColumns: "1fr" } : {}),
              }}
            >
              <div style={globalStyles.viewItem}>
                <div style={globalStyles.viewLabel}>Timestamp</div>
                <div style={globalStyles.viewValue}>
                  {view.timestamp
                    ? new Date(view.timestamp).toLocaleString()
                    : "—"}
                </div>
              </div>

              <div style={globalStyles.viewItem}>
                <div style={globalStyles.viewLabel}>Operation</div>
                <div style={globalStyles.viewValue}>
                  {formatLabel(view.action)}
                </div>
              </div>

              <div style={globalStyles.viewItem}>
                <div style={globalStyles.viewLabel}>Entity</div>
                <div style={globalStyles.viewValue}>
                  {view.entityType || "—"}
                </div>
              </div>

              <div style={globalStyles.viewItem}>
                <div style={globalStyles.viewLabel}>Entity ID</div>
                <div style={globalStyles.viewValue}>{view.entityId || "—"}</div>
              </div>

              <div style={globalStyles.viewItemFull}>
                <div style={globalStyles.viewLabel}>By</div>
                <div style={globalStyles.viewValue}>
                  {view.userId?.name || "—"}
                </div>
              </div>

              <div style={globalStyles.viewItemFull}>
                <div style={globalStyles.viewLabel}>Before</div>
                <pre style={s.pre}>
                  {view.before ? JSON.stringify(view.before, null, 2) : "—"}
                </pre>
              </div>

              <div style={globalStyles.viewItemFull}>
                <div style={globalStyles.viewLabel}>After</div>
                <pre style={s.pre}>
                  {view.after ? JSON.stringify(view.after, null, 2) : "—"}
                </pre>
              </div>
            </div>

            <div
              style={{
                ...globalStyles.mrow,
                ...(isMobile ? { justifyContent: "stretch" } : {}),
              }}
            >
              <button
                type="button"
                style={{
                  ...globalStyles.cancel,
                  ...(isMobile ? { width: "100%" } : {}),
                }}
                onClick={() => setView(null)}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
